
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { MdArrowBack, MdSave } from 'react-icons/md';
import './EditNumber.css';
import Swal from 'sweetalert2';
import axios from 'axios';
import { VIEW_NUMBER_API, UPDATE_NUMBER_API } from '../config/API';
import Loader from '../components/common/Loader';
import { showErrorToast } from '../utils/toastUtils';

const EditNumber = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [isViewOnly, setIsViewOnly] = useState(false);
    const [formData, setFormData] = useState({
        number: '',
        name: '' // Added name field
    });

    useEffect(() => {
        // Check if we are in view mode
        if (location.pathname.includes('/view/')) {
            setIsViewOnly(true);
        }

        // Always fetch from API
        fetchNumber();
    }, [id]);

    const fetchNumber = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('accessToken');
            const response = await axios.get(VIEW_NUMBER_API(id), {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.status) {
                setFormData({
                    number: response.data.data?.number || '',
                    name: response.data.data?.name || '' // Populate name from API
                });
            } else {
                showErrorToast('Failed to fetch number details');
            }
        } catch (error) {
            console.error('Error fetching number:', error);
            showErrorToast('An error occurred while fetching details');
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (isViewOnly) return;

        const result = await Swal.fire({
            title: 'Update Number?',
            text: 'Do you want to save changes to this number?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Yes, Update',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#4f46e5',
            buttonsStyling: false,
            customClass: {
                confirmButton: 'swal-confirm-btn',
                cancelButton: 'swal-cancel-btn',
            },
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const token = localStorage.getItem('accessToken');
                const response = await axios.put(UPDATE_NUMBER_API(id), formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });

                if (response.data.status) {
                    Swal.fire({
                        icon: 'success',
                        title: 'Updated!',
                        text: 'Number updated successfully',
                        timer: 2000,
                        showConfirmButton: false,
                    });
                    navigate('/settings');
                } else {
                    showErrorToast(response.data.error || response.data.message || 'Failed to update');
                }
            } catch (error) {
                console.error('Error updating:', error);
                const errorData = error.response?.data?.error || error.response?.data?.message || 'An error occurred';
                showErrorToast(errorData);
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return <Loader fullPage text="Loading..." />;
    }

    return (
        <div className="add-product-page">
            <div className="page-header">
                <button onClick={() => navigate('/settings')} className="back-btn">
                    <MdArrowBack />
                </button>
                <h2>{isViewOnly ? 'View Number Details' : 'Edit Number'}</h2>
            </div>

            <form onSubmit={handleSubmit} className="product-form">
                <div className="form-section">
                    <h3>Number Information</h3>
                    <div className="form-grid">

                        <div className="form-group">
                            <label>Name</label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                disabled={isViewOnly}
                                placeholder="e.g. John Doe"
                            />
                        </div>

                        <div className="form-group">
                            <label>Mobile Number <span className="required-star">*</span></label>
                            <input
                                type="text"
                                name="number"
                                value={formData.number}
                                onChange={handleChange}
                                disabled={isViewOnly}
                                placeholder="e.g. 9876543210"
                            />
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button type="button" onClick={() => navigate('/settings')} className="cancel-btn">
                        {isViewOnly ? 'Back' : 'Cancel'}
                    </button>
                    {!isViewOnly && (
                        <button type="submit" className="save-btn">
                            <MdSave /> Update Number
                        </button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default EditNumber;
